import { el, clear, fmtDate, relativeTime } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import {
  getMe,
  listPasskeys,
  enrollPasskey,
  renamePasskey,
  removePasskey,
  revokeAllSessions,
  browserSupportsWebAuthn,
} from '../api/auth';
import { UnauthorizedError } from '../api/client';
import { clearSession } from '../auth';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import type { Me, PasskeyRow } from '../api/types';

export async function renderSecurity({ root }: RouteContext) {
  const content = renderShell(root, 'security');
  const body = el('div', { class: 'detail-body' });
  content.append(
    el('button', { class: 'btn btn-ghost back-link', onClick: () => navigate('/') }, [
      '‹ All teams',
    ]),
    body,
  );

  async function load() {
    renderLoading(body, 'Loading account…');
    try {
      const [me, passkeys] = await Promise.all([getMe(), listPasskeys()]);
      renderContent(body, me, passkeys, load);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load account.');
    }
  }

  await load();
}

function renderContent(
  container: HTMLElement,
  me: Me,
  passkeys: PasskeyRow[],
  reload: () => void,
): void {
  clear(container);

  const isAdmin = me.role === 'ADMIN';
  const status = el('div', { class: 'banner banner-error', role: 'alert', hidden: true });
  const showStatus = (msg: string) => {
    status.textContent = msg;
    status.hidden = false;
  };

  // ── Identity ───────────────────────────────────────────────────────
  const identity = el('div', { class: 'detail-head' }, [
    el('div', {}, [
      el('h1', { textContent: me.name || me.email || 'Account' }),
      el('div', { class: 'cell-muted', textContent: me.email ?? '' }),
    ]),
    el('div', { class: 'detail-head-pills' }, [
      el('span', {
        class: isAdmin ? 'pill pill-ok' : 'pill pill-warn',
        textContent: me.role,
      }),
    ]),
  ]);

  const notAdminBanner = isAdmin
    ? null
    : el('div', { class: 'banner banner-error' }, [
        'This account does not have the ADMIN platform role, so the teams ' +
          'monitoring data will not load. Sign in with an admin account.',
      ]);

  // ── Add passkey ────────────────────────────────────────────────────
  const addBtn = el(
    'button',
    {
      class: 'btn btn-primary',
      type: 'button',
      disabled: !browserSupportsWebAuthn(),
    },
    ['Add a passkey'],
  );
  addBtn.addEventListener('click', async () => {
    status.hidden = true;
    const nickname = window.prompt('Name this passkey (optional):') ?? undefined;
    addBtn.disabled = true;
    addBtn.textContent = 'Follow the prompt…';
    try {
      await enrollPasskey(nickname?.trim() || undefined);
      reload();
    } catch (err) {
      const name = (err as { name?: string })?.name;
      showStatus(
        name === 'NotAllowedError' || name === 'AbortError'
          ? 'Passkey enrolment was cancelled.'
          : err instanceof Error
            ? err.message
            : 'Could not add passkey.',
      );
      addBtn.disabled = false;
      addBtn.textContent = 'Add a passkey';
    }
  });

  // ── Passkey list ───────────────────────────────────────────────────
  const list =
    passkeys.length === 0
      ? el('div', { class: 'empty' }, ['No passkeys yet. Add one to sign in without a token.'])
      : el('div', { class: 'table-wrap' }, [
          el('table', { class: 'data-table' }, [
            el('thead', {}, [
              el('tr', {}, ['Passkey', 'Added', 'Last used', ''].map((h) =>
                el('th', { textContent: h }),
              )),
            ]),
            el(
              'tbody',
              {},
              passkeys.map((p) => passkeyRow(p, reload, showStatus)),
            ),
          ]),
        ]);

  // ── Sessions ───────────────────────────────────────────────────────
  const signOut = el(
    'button',
    { class: 'btn btn-ghost', type: 'button', onClick: () => {
        clearSession();
        navigate('/login');
      } },
    ['Sign out'],
  );
  const signOutAll = el('button', { class: 'btn btn-ghost', type: 'button' }, [
    'Sign out everywhere',
  ]);
  signOutAll.addEventListener('click', async () => {
    if (!window.confirm('Revoke all sessions on every device?')) return;
    try {
      await revokeAllSessions();
    } catch {
      /* best-effort; we clear locally regardless */
    }
    clearSession();
    navigate('/login');
  });

  container.append(identity);
  if (notAdminBanner) container.append(notAdminBanner);
  container.append(
    status,
    el('section', { class: 'detail-section' }, [
      el('div', { class: 'section-head' }, [
        el('h2', { textContent: 'Passkeys' }),
        addBtn,
      ]),
      list,
    ]),
    el('section', { class: 'detail-section' }, [
      el('h2', { textContent: 'Sessions' }),
      el('div', { class: 'button-row' }, [signOut, signOutAll]),
    ]),
  );
}

function passkeyRow(
  p: PasskeyRow,
  reload: () => void,
  showStatus: (msg: string) => void,
): HTMLElement {
  const rename = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, ['Rename']);
  rename.addEventListener('click', async () => {
    const next = window.prompt('New name:', p.nickname ?? '');
    if (next === null) return;
    try {
      await renamePasskey(p.id, next.trim());
      reload();
    } catch (err) {
      showStatus(err instanceof Error ? err.message : 'Rename failed.');
    }
  });

  const remove = el('button', { class: 'btn btn-ghost btn-sm btn-danger', type: 'button' }, [
    'Remove',
  ]);
  remove.addEventListener('click', async () => {
    if (!window.confirm(`Remove passkey "${p.nickname || 'Unnamed'}"?`)) return;
    try {
      await removePasskey(p.id);
      reload();
    } catch (err) {
      showStatus(err instanceof Error ? err.message : 'Remove failed.');
    }
  });

  return el('tr', {}, [
    el('td', { class: 'cell-strong', textContent: p.nickname || 'Unnamed passkey' }),
    el('td', { class: 'cell-muted', textContent: fmtDate(p.createdAt) }),
    el('td', { class: 'cell-muted', textContent: relativeTime(p.lastUsedAt) }),
    el('td', { class: 'num' }, [el('div', { class: 'button-row' }, [rename, remove])]),
  ]);
}
